import { useEffect } from "react";
import type { ReportSubTab } from "../types";
import type { ReportValidationIssue } from "../reportConfigurationValidation";

interface ReportValidationDialogProps {
    open: boolean;
    issues: ReportValidationIssue[];
    onClose: () => void;
    onOpenIssue: (issue: ReportValidationIssue) => void;
}

const TAB_LABELS: Record<ReportSubTab, string> = {
    setup: "Setup",
    categorization: "Categorization",
    calculated: "Calculated Fields",
    metrics: "Metrics",
    tripdata: "Trip Data & Exclusions",
    excel: "Excel Layout",
    exclusions: "Excluded Record Summary",
    other: "Call Center & Exclusions",
};

const TAB_ORDER: ReportSubTab[] = ["setup", "categorization", "calculated", "metrics", "excel", "tripdata", "other"];

function groupIssues(issues: ReportValidationIssue[]) {
    return TAB_ORDER
        .map((tab) => ({
            tab,
            issues: issues.filter((issue) => issue.tab === tab),
        }))
        .filter((group) => group.issues.length);
}

export function ReportValidationDialog({ open, issues, onClose, onOpenIssue }: ReportValidationDialogProps) {
    useEffect(() => {
        if (!open) return undefined;
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [onClose, open]);

    if (!open) return null;

    const errors = issues.filter((issue) => issue.severity === "error");
    const warnings = issues.filter((issue) => issue.severity === "warning");
    const grouped = groupIssues(issues);
    const firstIssue = errors[0] || issues[0];
    const hasErrors = errors.length > 0;

    return (
        <div className="rc-confirm-layer rc-validation-layer" role="presentation" onMouseDown={onClose}>
            <section
                aria-labelledby="rc-validation-title"
                aria-modal="true"
                className="rc-confirm-panel rc-validation-panel"
                role="dialog"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="rc-confirm-visual" aria-hidden="true">
                    <span className="rc-confirm-orbit rc-validation-orbit">
                        <span className="material-icons md-18">fact_check</span>
                    </span>
                </div>
                <div className="rc-confirm-copy">
                    <span className="rc-confirm-kicker rc-validation-kicker">Review before saving</span>
                    <h3 id="rc-validation-title">{hasErrors ? "Complete Required Fields" : "Review Warnings"}</h3>
                    <p>
                        {hasErrors
                            ? `${errors.length} required item${errors.length === 1 ? "" : "s"} need attention before this report configuration can be saved.`
                            : `${warnings.length} warning${warnings.length === 1 ? "" : "s"} found. Warnings do not block saving.`}
                    </p>
                </div>
                <button className="rc-confirm-close" type="button" aria-label="Close validation summary" onClick={onClose}>
                    <span className="material-icons md-18" aria-hidden="true">close</span>
                </button>

                <div className="rc-validation-summary">
                    <span className="rc-validation-count error">{errors.length} Error{errors.length === 1 ? "" : "s"}</span>
                    <span className="rc-validation-count warning">{warnings.length} Warning{warnings.length === 1 ? "" : "s"}</span>
                </div>

                <div className="rc-validation-list">
                    {grouped.map((group) => (
                        <section className="rc-validation-group" key={group.tab}>
                            <h4>
                                <span>{TAB_LABELS[group.tab]}</span>
                                <span className={group.issues.some((issue) => issue.severity === "error") ? "rc-validation-group-count error" : "rc-validation-group-count warning"}>
                                    {group.issues.length}
                                </span>
                            </h4>
                            <ul>
                                {group.issues.map((issue) => (
                                    <li key={issue.id} className={`rc-validation-issue ${issue.severity}`}>
                                        <span className="material-icons md-16" aria-hidden="true">{issue.severity === "error" ? "error" : "warning"}</span>
                                        <span className="rc-validation-issue-copy">
                                            <strong>{issue.title}</strong>
                                            <small>{issue.message}</small>
                                        </span>
                                        <button className="rc-validation-open" type="button" onClick={() => onOpenIssue(issue)}>
                                            Open
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>

                <div className="rc-confirm-actions">
                    <button className="rc-confirm-button rc-confirm-cancel" type="button" onClick={onClose}>
                        Close
                    </button>
                    {firstIssue && (
                        <button className="rc-confirm-button rc-confirm-primary" type="button" onClick={() => onOpenIssue(firstIssue)}>
                            <span className="material-icons md-16" aria-hidden="true">near_me</span>
                            <span>Go To First Issue</span>
                        </button>
                    )}
                </div>
            </section>
        </div>
    );
}
